/**
 * Typed Endpoint Access - type utilities for strongly-typed endpoint access
 *
 * Supports object-based endpoint definitions:
 *   const endpoints = {
 *     volume: { dataType: 'number', value: -12 },
 *     tick: { dataType: 'number', dataFlow: 'trigger', isOutput: true },
 *   };
 */

import { DataTypeSpec, DataFlow, Endpoint } from './Endpoint';
import { EnvelopeData } from './EnvelopeData';

/** RGBA color type (values 0-1) */
export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}
import { Payload, PayloadMetadata } from '../graph/Payload';

/**
 * Endpoints object type - maps id to config (preserves specific types for filtering).
 * The id is optional since it's derived from the object key at runtime.
 */
export type EndpointsRecord = Record<string, Endpoint<DataTypeSpec, DataFlow>>;

// ─────────────────────────────────────────────────────────────
// Type Mapping
// ─────────────────────────────────────────────────────────────

/**
 * Map DataType to TypeScript types.
 */
export type DataTypeToTS = {
  number: number;
  string: string;
  boolean: boolean;
  array: any[];
  envelope: EnvelopeData;
  audio: any;
  video: any;
  hydra: any;
  three: any;
  two: any;
  nodebox: any;
  color: RGBA;
};

/**
 * Extract the TypeScript type for an endpoint based on its dataType.
 * For String types with options, uses the options to create a union type.
 * Defaults to number if dataType is not specified.
 */
export type EndpointValueType<E extends Endpoint> =
  E['dataType'] extends 'string'
    ? E extends { options: readonly (infer O)[] }
      ? O
      : string
    : E['dataType'] extends keyof DataTypeToTS
      ? DataTypeToTS[E['dataType']]
      : E['dataType'] extends undefined
        ? number  // default to number
        : any;

// ─────────────────────────────────────────────────────────────
// Param Accessor
// ─────────────────────────────────────────────────────────────

/**
 * Param accessor - combines value, setter, and metadata for a single endpoint.
 * The set() method accepts either a raw value or a Payload object (for preserving time metadata).
 */
export interface ParamAccessor<T> {
  readonly value: T;
  set(value: T | Payload): void;
  readonly dataType: string;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
}

/**
 * Create typed param accessors for data endpoints.
 * Excludes audio (stream dataType) and event/trigger (flow types).
 * Endpoints without dataType default to 'number'.
 */
export type TypedParams<T extends EndpointsRecord> = {
   readonly [K in keyof T as T[K]['dataFlow'] extends 'param' ? K : never]: ParamAccessor<EndpointValueType<T[K]>>;
};

/** Extract just the keys of param endpoints */
export type ParamKeys<T extends EndpointsRecord> = keyof TypedParams<T>;

export type TriggerKeys<T extends EndpointsRecord> = keyof TypedTriggers<T>;

export type EventKeys<T extends EndpointsRecord> = keyof TypedEvents<T>;

export type InternalKeys<T extends EndpointsRecord> = keyof TypedInternal<T>;

export  type EndpointKeys<T extends EndpointsRecord> = ParamKeys<T> |  EventKeys<T> | TriggerKeys<T> | InternalKeys<T>;   

export interface InternalAccessor<T> {
  readonly value: T;
  set(value: T | Payload): void;
  readonly dataType: string;
}

export type TypedInternal<T extends EndpointsRecord> = {
   readonly [K in keyof T as T[K]['dataFlow'] extends 'internal' ? K : never]: InternalAccessor<EndpointValueType<T[K]>>;
};

// ─────────────────────────────────────────────────────────────
// Trigger & Event Emitters
// ─────────────────────────────────────────────────────────────

/**
 * Trigger emitter - provides emit() for trigger endpoints.
 */
export interface TriggerEmitter {
  emit(metadata?: PayloadMetadata): void;
}

/**
 * Event emitter - provides emit(payload) for event endpoints.
 */
export interface EventEmitter {
  emit(payload: Payload): void;
}

/**
 * Create typed trigger accessors.
 */
export type TypedTriggers<T extends EndpointsRecord> = {
  readonly [K in keyof T as T[K]['dataFlow'] extends 'trigger' ? K : never]: TriggerEmitter;
};

/**
 * Create typed event accessors.
 */
export type TypedEvents<T extends EndpointsRecord> = {
  readonly [K in keyof T as T[K]['dataFlow'] extends 'event' ? K : never]: EventEmitter;
};

// ─────────────────────────────────────────────────────────────
// Audio Connector
// ─────────────────────────────────────────────────────────────

/**
 * Audio connector - provides connect/disconnect for audio endpoints.
 * Node uses this to wire internal audio to/from endpoint GainNodes.
 * Handles Tone.js bridging automatically when transport context is available.
 */
export interface AudioConnector {
  /** Connect internal node to this endpoint (input: to receive, output: to send) */
  connect(node: any): void;
  /** Disconnect internal node from this endpoint */
  disconnect(node: any): void;
}

/**
 * Create typed audio accessors.
 */
export type TypedAudio<T extends EndpointsRecord> = {
  readonly [K in keyof T as T[K]['dataType'] extends 'audio' ? K : never]: AudioConnector;
};

/**
 * Helper to define endpoints with preserved literal types.
 * Applies default dataType: 'number' if not specified.
 *
 * @example
 * const endpoints = defineEndpoints({
 *   volume: { value: -12 },  // dataType defaults to 'number'
 *   event: { dataFlow: 'event', isOutput: true },  // dataType defaults to 'number'
 * });
 */
/*
export function defineEndpoints<T extends EndpointsRecord>(endpoints: T): T {
  // Apply defaults: dataType: 'number', dataFlow: 'param'
  const result = {} as T;
  for (const [id, endpoint] of Object.entries(endpoints)) {
    result[id as keyof T] = {
      ...endpoint,
      dataType: endpoint.dataType ?? 'number',
      dataFlow: endpoint.dataFlow ?? 'param'
    } as T[keyof T];
  }
  return result;
}

*/
export type AudioEndpoint = {
  dataFlow: 'stream',
  dataType: 'audio'
}


export type VideoEndpoint = {
  dataFlow: 'internal',
  dataType: 'video'
}


/*
NB data flow is internal, not stream
*/
/*
export function video<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: VideoEndpoint } {
  return Object.fromEntries(
    Object.entries(p).map(([key, val]) => [key, {
      ...val,
      dataFlow: 'internal' as DataFlow,
      dataType: 'video' as DataTypeSpec
    }])
  ) as { [K in keyof T]: VideoEndpoint };
}*/


export function audio<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: AudioEndpoint } {
  return Object.fromEntries(
    Object.entries(p).map(([key, val]) => [key, {
      ...val,
      dataFlow: 'stream' as DataFlow,
      dataType: 'audio' as DataTypeSpec
    }])
  ) as { [K in keyof T]: AudioEndpoint };
}

export type ParamEndpoint<T> = T & {
  dataFlow: 'param';
  dataType: T extends { dataType: infer D extends DataTypeSpec } ? D : 'number';
};
export function params<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: ParamEndpoint<T[K]> } {
  return Object.fromEntries(
    Object.entries(p).map(([key, p]) => {
      const dataType = p.dataType ?? 'number';
      const isNumber = dataType === 'number';
      const isArray = dataType === 'array';
      return [key, {
        ...p,
        dataFlow: 'param' as DataFlow,
        dataType: dataType as DataTypeSpec,
        useExp: p.useExp || false,
        ...(isNumber && {
          value: p.value ?? 0,
          min: p.min ?? 0,
          max: p.max ?? 100,
          step: p.step ?? 1
        }),
        ...(isArray && p.value === undefined && {
          value: [],
        })
      }];
    })
  ) as { [K in keyof T]: ParamEndpoint<T[K]> };
}


export type TriggerEndpoint = {
  dataFlow: 'trigger';
  dataType: 'boolean';
};

export function triggers<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: TriggerEndpoint } {
  return Object.fromEntries(
    Object.entries(p).map(([key, val]) => [key, {
      ...val,
      dataFlow: 'trigger' as DataFlow,
      dataType: 'boolean' as DataTypeSpec
    }])
  ) as { [K in keyof T]: TriggerEndpoint };
}


export type EventEndpoint<T> = T & {
  dataFlow: 'event';
  dataType: T extends { dataType: infer D extends DataTypeSpec } ? D : 'number';
};

//dataType: 'number', dataFlow: 'event', isInput: true, isOutput: true, allowInput: false, allowOutput: false, primary: true 
export function events<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: EventEndpoint<T[K]> } {
  return Object.fromEntries(
    Object.entries(p).map(([key, val]) => [key, {
      allowInput: false, 
      allowOutput: false, 
      ...val,
      dataFlow: 'event' as DataFlow,
      dataType: val.dataType ?? 'number' as DataTypeSpec,
    }])
  ) as { [K in keyof T]: EventEndpoint<T[K]> };
}

export type InternalEndpoint<T> = Omit<T, 'dataFlow'> & {
  dataFlow: 'internal';
};

/** Default accepts for input endpoints by dataType */
const inputAccepts: Partial<Record<string, string[]>> = {
  hydra: ['hydra', 'video', 'polylines'],
  video: ['video', 'hydra', 'polylines'],
};

/** Default stringify for array internals: shows length + value preview */
function stringifyArray(value: any): string {
  if (!Array.isArray(value) || value.length === 0) return '';
  const preview = value.slice(0, 4).map(v =>
    typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : String(v)
  ).join(', ');
  const suffix = value.length > 4 ? ', \u2026' : '';
  return `[${value.length}] ${preview}${suffix}`;
}

export function internals<T extends Record<string, Partial<Endpoint>>>(p: T): { [K in keyof T]: InternalEndpoint<T[K]> } {
  return Object.fromEntries(
    Object.entries(p).map(([key, val]) => {
      const dataType = (val.dataType ?? 'number') as DataTypeSpec;
      const accepts = val.accepts ?? (val.isInput && inputAccepts[dataType]);
      return [key, {
        ...val,
        dataFlow: 'internal' as DataFlow,
        dataType,
        ...(accepts && { accepts }),
        value: val.value !== undefined ? val.value
          : val.dataType === 'array' ? []
          : val.isOutput ? []
          : val.value,
        ...(dataType === 'array' && !val.stringify && { stringify: stringifyArray }),
      }];
    })
  ) as { [K in keyof T]: InternalEndpoint<T[K]> };
}